import { createApi } from '@reduxjs/toolkit/query/react'
import { transformResponse, baseQuery } from '../../helpers/rtk-query-tools';
import Cookies from 'js-cookie';
import Config from '../../../app.config';

export interface LoginArgs {
  username: string
  password: string
}

export interface GetCustomerDataArgs {
  noCache?: boolean
}

export interface RateSongArgs {
  like: boolean
  songId: number
  stationId: number
  comment: string
}

export const apiAuth = createApi({
  reducerPath: 'apiAuth',
  baseQuery,
  endpoints: builder => ({
    login: builder.mutation<any, LoginArgs>({
      query: args => ({
        url: 'customer_service/authenticate',
        method: 'POST',
        body: {
          data: {
            Customer: {
              username: args.username,
              password: args.password
            }
          }
        }
      }),
      transformResponse
    }),
    getCustomerData: builder.query<any, GetCustomerDataArgs>({
      query: args => `customer_service/get_customer_data${args.noCache === true ? '/1' : ''}`,
      forceRefetch: () => true,
      transformResponse
    }),
    rateSong: builder.mutation<any, RateSongArgs>({
      query: args => ({
        url: `customer_service/${args.like ? 'like_song' : 'do_not_like_song'}/${args.songId}/${args.stationId}`,
        method: 'POST',
        body: {
          comment: args.comment
        }
      }),
      transformResponse
    })
  })
});

export const getTMSSession = (): string | undefined => {
  return Cookies.get('KMSSESSION');
};

export const hasTMSSession = (): boolean => {
  return Cookies.get('KMSSESSION') !== undefined;
};

export const removeTMSSession = (): void => {
  Cookies.remove('KMSSESSION', {
    domain: Config.cookieDomain,
    path: '/'
  });
};

interface RequestState {
  isError?: boolean
  data?: { ok?: boolean, code?: string }
}

const invalidCredentialCodes = ['authentication_failed', 'not_authenticated'];

// Failures can arrive as a 200 with a failure code or as an RTK Query error (non-2xx, CORS, network).
export const getLoginErrorKey = (login: RequestState, customerData: RequestState): string | null => {
  if (login.data?.ok === false && invalidCredentialCodes.includes(login.data.code ?? '')) {
    return 'authentication.invalid_credentials';
  }
  if (login.isError || login.data?.ok === false || customerData.isError || customerData.data?.ok === false) {
    return 'authentication.login_failed';
  }
  return null;
};

export const isKnownUser = (): boolean => {
  const knownUser = Cookies.get('WEBUI_KNOWN_USER') === 'true';
  if (knownUser) {
    Cookies.set('WEBUI_KNOWN_USER', 'true', { expires: 400 });
  }
  return knownUser;
};

export const toggleKnownUser = (): void => {
  Cookies.set('WEBUI_KNOWN_USER', 'true', { expires: 400 });
};

export const {
  useLoginMutation,
  useGetCustomerDataQuery,
  useLazyGetCustomerDataQuery,
  useRateSongMutation
} = apiAuth;
